/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import React from "react";
import LayoutFullWidth from "@/app/components/layouts/LayoutFullWidth";
import {
    STATIC_DATA_REVALIDATE_PERIOD_SECONDS
} from "@/app/constants";
import {getCommonStaticProps} from "@/app/utils/getStaticProps/getCommonStaticProps";
import {TCommonStaticDataObject} from "@/app/types/prefetchedData/commonStaticData";
import getMaintenanceMode from "@/app/utils/getMaintenanceMode";
import {GetStaticProps} from "next";
import {getBrandsShortStaticProps} from "@/app/utils/getStaticProps/getBrandsShortStaticProps";
import {TBrandsShortData} from "@/app/types/brands/brandTypes";
import {useSnackbar} from "notistack";
import {useTranslation} from "next-i18next";
import {useAppSelector} from "@/app/hooks/redux";
import {brandsConfig} from "@/app/config/brandsConfig";
import BrandsListView from "@/app/components/brands/BrandsListView";

export const getStaticProps: GetStaticProps = async (context) => {
    return {
        props: {
            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
            brandsPageData: await getBrandsShortStaticProps(),
            maintenanceMode: await getMaintenanceMode(),
            commonStaticData: await getCommonStaticProps()
        },
        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
    }
}

export default function BrandsListPage (props: {
    brandsPageData: TBrandsShortData,
    maintenanceMode?: boolean,
    commonStaticData: TCommonStaticDataObject
}) {
    const {t} = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const {storeConfig} = useAppSelector(state => state.storeConfigReducer);

    if(!props.brandsPageData || props.brandsPageData.isNetworkError) {
        enqueueSnackbar(t('Network error. ') + props.brandsPageData?.errorMessage ?? '', {variant: "error"});
    }

    return (
        <>
            <LayoutFullWidth
                headTitle={storeConfig?.brand_listing_page_title ?? brandsConfig.defaultValues.brandsListTitle}
                metaDescription={t('Discover a list of available brands')}
                mainContainerClassName={`brands-list-page`}
                canonical={`/brands.html`}
            >
                {props.brandsPageData.brands && props.brandsPageData.brands.length > 0 ?
                    <BrandsListView brands={props.brandsPageData.brands}/> :
                    <p>{t('No brands available')}</p>
                }
            </LayoutFullWidth>
        </>
    );
}